import { useNavigate } from 'react-router-dom';

import { pick } from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import Intercom from '@intercom/messenger-js-sdk';
import { Honeybadger } from '@honeybadger-io/react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { getPracticeModules } from 'api/Practice';

import connectToSocket from 'components/sockets/connectToSocket';

import { io } from '../helpers/utility';
import state from '../../components/state';
import { interimApi } from '../../api/InterimApi';
import { defaultSettings } from '../constants/constants';
import { requestApi, setActiveSession } from '../../api/Api';
import adminState from '../../components/admin/lib/adminState';
import { bootPatientIntercom } from '../helpers/intercomPatient';
import practiceState from '../../components/practice/practiceState';
import { findOpenAndCloseClinicHours } from '../helpers/workingHours';
import { getSessionReplayAttrs } from '../helpers/intercomSessionReplay';

const useProcessLogin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(state.userState);
  const [admin, _setAdminUser] = useRecoilState(adminState.current_admin);
  const [, setPractice] = useRecoilState(practiceState.currentPractice);
  const [permissions, setPermissions] = useRecoilState(state.permissions);
  const setModules = useSetRecoilState(state.modulesState);
  const setHasBeenAuthChecked = useSetRecoilState(state.hasBeenAuthChecked);
  const setAppointmentFilters = useSetRecoilState(practiceState.calendarFilters);
  const setSocketSessionToken = useSetRecoilState(state.socketSessionTokenState);
  const [_, setOwnerSelectedLocation] = useRecoilState(practiceState.ownerSelectedLocation);

  const getPermissions = async () => {
    if (
      permissions.loaded ||
      (!(user.logged_in === admin.logged_in) && (user.logged_in || admin.logged_in))
    ) {
      return;
    }

    const onSuccess = (data) => {
      if (data?.permissions) setPermissions({ ...data?.permissions, loaded: true });
    };
    await requestApi({ url: '/api/permissions/get', navigate, onSuccess });
  };

  const getModules = async () => {
    if (admin.logged_in) return;
    const data = await getPracticeModules(navigate);
    setModules({ ...(data?.modules ?? {}), loaded: true });
  };

  const getCurrentPractice = async () => {
    try {
      const res = await interimApi('/api/practice/current_practice', {}, navigate);
      const { practice, selected_practice_id } = res.data;
      if (practice) {
        const workingHours = findOpenAndCloseClinicHours(practice?.available_times);
        setPractice({
          ...practice,
          ...pick(practice, [
            'id',
            'name',
            'email',
            'phone',
            'header_photo',
            'timezone',
            'display_settings',
            'appointment_types',
            'care_credit',
            'surcharge',
            'surcharge_percentage',
            'surcharge_enabled'
          ]),
          address: practice?.practiceAddress?.fullAddress,
          working_hours: workingHours,
          elab_orders: !!practice?.labsoft_cust_id
        });
        if (selected_practice_id) {
          setOwnerSelectedLocation(selected_practice_id);
        }
      }
      if (!practice) {
        setPractice({});
      }
      return practice ? { id: practice.id, name: practice.name } : null;
    } catch (error) {
      Honeybadger.notify(`There's been an unexpected error, please try again later. ${error}`);
    }
  };

  const processLogin = async ({ user, redirect, navigateToLast, session_token }) => {
    setActiveSession(true);
    setUser({
      ...user,
      authenticated: true,
      display_settings: user?.display_settings || defaultSettings,
      logged_in: true,
      wizard_onboarding_id: user?.wizard_onboarding_id
    });
    await getPermissions();
    const currPractice = await getCurrentPractice();
    if (currPractice?.id) await getModules();
    if (session_token) {
      setSocketSessionToken(session_token);
      connectToSocket({ sessionToken: session_token });
    }

    try {
      const newUser = {
        id: user?.id,
        name: `${user?.f_name} ${user?.l_name}`,
        email: user?.email,
        admin: false,
        created_at: user?.created_at,
        phone: user?.phone,
        role: user?.role
      };

      if (currPractice) {
        newUser['practiceId'] = currPractice?.id;
        newUser['practiceName'] = currPractice?.name;
      }

      datadogRum.setUser(newUser);

      // start intercom only for practice staff, not patients
      if (currPractice?.id) {
        Intercom({
          hide_default_launcher: true,
          custom_launcher_selector: '[data-intercom-trigger="true"]',
          app_id: process.env.INTERCOM_APP_ID,
          email: newUser?.email,
          phone: newUser?.phone,
          user_id: newUser?.id,
          created_at: newUser?.created_at,
          name: newUser?.name,
          practiceName: currPractice?.name,
          practiceID: currPractice?.id,
          profileType: newUser?.role,
          ...getSessionReplayAttrs()
        });
      } else {
        bootPatientIntercom({
          user: {
            id: newUser?.id,
            email: newUser?.email,
            phone: newUser?.phone,
            f_name: user?.f_name,
            l_name: user?.l_name,
            created_at: newUser?.created_at
          }
        });
      }
    } catch (e) {
      console.error(e);
    }

    try {
      Honeybadger.setContext({
        user_id: user?.user_id,
        user_email: user?.email,
        practice_name: currPractice?.name,
        practice_id: currPractice?.id
      });
    } catch (error) {
      console.error(error);
    }
    if (user?.display_settings && io(user?.display_settings?.appointmentFilters)) {
      setAppointmentFilters(user?.display_settings?.appointmentFilters);
    }
    setHasBeenAuthChecked(() => ({ isImpersonate: false, checked: true }));
    if (navigateToLast) {
      navigateToLast(redirect);
    } else {
      navigate(redirect);
    }
  };

  return processLogin;
};

export default useProcessLogin;
